import React from 'react'
import { Tooltip } from 'react-tooltip'
import styled from 'styled-components'
import Row from '@/components/common/Row'
import Icon from '@/components/common/Icon'
import classNames from 'classnames'
import Button from '@/components/ui/Button'
import { useTranslations } from 'next-intl'

const CustomTooltipContainer = styled.div`
  background-color: #fff;
  border-radius: 10px;
  display: flex;
  gap: 4px;
  flex-direction: column;
`
const CustomTooltipContainerTop = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;

  &.tooltip-class {
    align-items: flex-start;
  }

  &.with-icon {
    display: grid;
    grid-template-columns: 16px 1fr;
    gap: 8px;
  }
`

const CustomTooltipTitle = styled.h4`
  font-size: 12px;
  line-height: 15px;
  word-break: break-word;
`

const CustomText = styled.div`
  font-size: 16px;
  word-break: break-word;
  width: fit-content;
  max-width: 300px;
  white-space: wrap;
  color: rgba(122, 122, 122, 1);
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;

  &.tooltip-description {
    color: rgba(106, 103, 218, 1);
  }
`
const Wrap = styled.div`
  width: fit-content;
`

const CustomButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  margin-top: 16px;
`
const ButtonWrap = styled(Button)`
  padding: 4px 8px;
  border-radius: 12px;
  width: 100%;
  font-size: 15px;
  font-weight: 500;
  line-height: 19.36px;
`

const TooltipInfoWrap = styled(Row)`
  .custom-tooltip {
    min-width: 300px;
    max-width: 310px;
    padding: 12px;
    border-radius: 12px;
    background-color: rgba(255, 255, 255, 1);
    color: var(--black);
    z-index: 999;
    opacity: 1;
    font-size: 16px;
    filter: drop-shadow(1px 2px 4px rgba(0, 0, 0, 0.1));
    line-height: 1.2em;
    font-weight: 500;
  }

  &.small {
    .custom-tooltip {
      width: 165px;
      min-width: 165px;
    }
  }

  &.clickable {
    .custom-tooltip {
      pointer-events: initial;
    }
  }
`

const CustomTooltip = ({
  id,
  effect = 'solid',
  place = 'top',
  delayShow = 300,
  delayHide = 0,
  tooltipLink,
  children,
  tooltipContent,
  tooltipPinnedActions,
  withButton = false,
  small,
  tooltipTitle,
  iconName,
  onButtonCancel = () => {},
  onButtonDelete,
  onButtonUnpin,
  onTooltipStateChange,
  onButtonClear,
  isError = undefined,
  events = ['hover'],
  isClickable = false,
  stopPropogation,
  isContentWithTooltip = true,
  onButtonApply,
  ...props
}) => {
  const t = useTranslations()

  const tooltipClasses = classNames({
    'tooltip-class': tooltipContent,
    'with-icon': iconName,
  })

  const tooltipDescriptionClass = classNames({
    'tooltip-description': tooltipLink,
  })

  const tooltipWrapClass = classNames({
    small: small,
    clickable: isClickable,
  })

  const stopEventBubble = (e) => {
    if (stopPropogation) {
      e.stopPropagation()
    }
  }

  const onButtonCancelHandler = (e) => {
    stopEventBubble(e)
    onButtonCancel()
  }
  const onButtonClearHandler = (e) => {
    stopEventBubble(e)
    onButtonClear()
  }
  const onButtonDeleteHandler = (e) => {
    stopEventBubble(e)
    onButtonDelete(e)
  }
  const onButtonUnpinHandler = (e) => {
    stopEventBubble(e)
    onButtonUnpin(e)
  }

  const handleTooltipOpen = () => {
    if (onTooltipStateChange) {
      onTooltipStateChange(true)
    }
  }

  const handleTooltipClose = () => {
    if (onTooltipStateChange) {
      onTooltipStateChange(false)
    }
  }

  const onButtonSubmit = (e) => {
    stopEventBubble(e)
    onButtonApply(e)
  }

  return (
    <Wrap className="custom-wrap">
      {isContentWithTooltip && <div data-tooltip-id={id}>{children}</div>}
      <TooltipInfoWrap className={tooltipWrapClass}>
        <Tooltip
          id={id}
          effect={effect}
          place={place}
          delayShow={delayShow}
          isOpen={isError}
          delayHide={delayHide}
          className="custom-tooltip"
          events={events}
          afterShow={handleTooltipOpen}
          afterHide={handleTooltipClose}
          {...props}
        >
          <CustomTooltipContainerTop className={tooltipClasses}>
            {iconName && (
              <Icon
                className="tooltip-icon"
                icon={iconName}
                size="16"
                color="grey"
              />
            )}
            <CustomTooltipContainer>
              <CustomTooltipTitle>{tooltipTitle}</CustomTooltipTitle>
              {tooltipContent && (
                <CustomText
                  className={tooltipDescriptionClass}
                  weight="400"
                  dangerouslySetInnerHTML={{ __html: tooltipContent }}
                ></CustomText>
              )}
              {tooltipPinnedActions && withButton && (
                <CustomButtonWrapper>
                  <ButtonWrap onClick={onButtonCancelHandler} block>
                    {t('common.mainCancel')}
                  </ButtonWrap>
                  <ButtonWrap onClick={onButtonClearHandler} filled block>
                    {t('creatives.clear')}
                  </ButtonWrap>
                  <ButtonWrap
                    onClick={(e) => onButtonUnpinHandler(e)}
                    buttonType="purple"
                    filled
                    block
                  >
                    {t('creatives.unpin')}
                  </ButtonWrap>
                </CustomButtonWrapper>
              )}
              {!tooltipPinnedActions && withButton && (
                <CustomButtonWrapper>
                  <ButtonWrap onClick={onButtonCancelHandler} block>
                    {t('common.mainCancel')}
                  </ButtonWrap>
                  {onButtonDelete && (
                    <ButtonWrap onClick={onButtonDeleteHandler} filled block>
                      {t('common.mainDelete')}
                    </ButtonWrap>
                  )}
                  {onButtonApply && (
                    <ButtonWrap onClick={onButtonSubmit} filled block>
                      {t('favorites.confirm')}
                    </ButtonWrap>
                  )}
                </CustomButtonWrapper>
              )}
            </CustomTooltipContainer>
            {!isContentWithTooltip && children}
          </CustomTooltipContainerTop>
        </Tooltip>
      </TooltipInfoWrap>
    </Wrap>
  )
}

export default CustomTooltip
